import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Timestamp } from 'firebase/firestore';
import moment from 'moment';
import { delay, firstValueFrom, map, Observable } from 'rxjs';
import PaginationResult from 'src/app/core/domain/pagination-result';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { UserId } from '../../auth/domain/user';
import {
  AccountStatus,
  Gender,
  Patient,
  PatientProps,
} from '../domain/patient';
import { PatientNotFoundException } from '../domain/patient_exceptions';

interface PatientSchema {
  userId: string | null;
  dietId: string;
  firstName: string;
  lastName: string;
  email: string;
  goals?: string[];
  phoneNumber: string;
  nextAppointment: Timestamp | null;
  birthday: Timestamp | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  archivedAt: Timestamp | string | null;
  groupId: string[] | null;
  lastActiveDate: Timestamp | null;
  gender: Gender | null;
  avatarUrl: string | null;
  consultationReason: string | null;
  accountStatus: AccountStatus;
  isActive: boolean;
  unreadDiariesCount: number;
  lastUnreadDiaryCreatedAt: Timestamp | null;
  msdpId: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  isSharedSeries: boolean | null;
  notificationWebToken: string | null;
}

export interface PatientCounter {
  activated: number;
  archived: number;
}

export class ResultAlternativeLoginPatient {
  dietitianFullName?: string;
  dietitianId?: string;
  patientId?: string;
}

export class ResultAlternativeLoginAction {
  email?: string;
  phoneNumber?: string;
  items?: ResultAlternativeLoginPatient[];
}

@Injectable()
export class PatientRepository {
  private readonly searchPatientsFromCloud: (
    data: unknown,
  ) => Observable<PaginationResult<{ [key: string]: unknown }>>;
  private indexationPatientFromCloud: (data: unknown) => Observable<void>;
  private indexationPatientsFromCloud: (data: unknown) => Observable<void>;
  private createPatientInvitationNotificationFromCloud: (
    data: unknown,
  ) => Observable<void>;
  private readonly processPostLoginAction: (
    data: unknown,
  ) => Observable<{ [key: string]: unknown }>;
  private readonly processPostAlternativeLoginAction: (
    data: unknown,
  ) => Observable<{ [key: string]: unknown }>;
  private readonly closeAccountFromCloud: (data: unknown) => Observable<void>;
  private checkPhoneNumberFromCloud: (
    data: unknown,
  ) => Observable<{ check: boolean }>;
  public registerPatientForSurvey: (
    data: unknown,
  ) => Observable<string | undefined>;
  public loginPatientForSurvey: (data: unknown) => Observable<string>;

  private static readonly PATIENT_INDEX = 'patients';
  private static readonly PATIENT_CREATED_AT_DESC_INDEX =
    'patients_createdAt_desc';
  private static readonly PATIENT_LAST_ACTIVE_DATE_DESC_INDEX =
    'patients_lastActiveDate_desc';

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
  ) {
    this.searchPatientsFromCloud = this.functions.httpsCallable<
      unknown,
      PaginationResult<{ [key: string]: unknown }>
    >('patient-searchDietitianPatients');
    this.indexationPatientFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('patient-indexationPatient');
    this.indexationPatientsFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('patient-indexationPatients');
    this.createPatientInvitationNotificationFromCloud =
      this.functions.httpsCallable<unknown, void>(
        'patient-createPatientInvitationNotification',
      );
    this.registerPatientForSurvey = this.functions.httpsCallable<
      unknown,
      string | undefined
    >('patient-registerPatientForSurvey');
    this.loginPatientForSurvey = this.functions.httpsCallable<unknown, string>(
      'patient-loginPatientForSurvey',
    );
    this.processPostLoginAction = this.functions.httpsCallable<
      unknown,
      { [key: string]: unknown }
    >('patient-processPostLoginAction');
    this.closeAccountFromCloud = this.functions.httpsCallable<unknown, void>(
      'patient-closePatientAccount',
    );
    this.checkPhoneNumberFromCloud = this.functions.httpsCallable<
      unknown,
      { check: boolean }
    >('patient-checkPhoneNumber');
    this.processPostAlternativeLoginAction = this.functions.httpsCallable<
      unknown,
      { [key: string]: unknown }
    >('patient-processAlternativeAuthentication');
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<PatientSchema>('patients', queryFn);
  }

  private collectionProps(queryFn?: QueryFn) {
    return this.firestore.collection<PatientProps>('patients', queryFn);
  }

  toSchema(patient: Patient): PatientSchema {
    return <PatientSchema>{
      userId: patient.userId?.id.toString() ?? null,
      dietId: patient.dietId,
      firstName: patient.firstName,
      lastName: patient.lastName,
      email: patient.email ?? null,
      phoneNumber: patient.phoneNumber ?? null,
      nextAppointment:
        patient.nextAppointment !== undefined
          ? Timestamp.fromDate(patient.nextAppointment)
          : null,
      birthday:
        patient.birthday !== undefined
          ? Timestamp.fromDate(patient.birthday)
          : null,
      goals: patient.goals ?? null,
      createdAt:
        patient.createdAt !== undefined
          ? Timestamp.fromDate(patient.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      // algolia doesn't handle null or undefined
      archivedAt:
        patient.archivedAt !== undefined
          ? Timestamp.fromDate(patient.archivedAt)
          : 'null',
      // algolia doesn't handle null or undefined
      groupId: patient.groupId ?? 'null',
      lastActiveDate:
        patient.lastActiveDate !== undefined
          ? Timestamp.fromDate(patient.lastActiveDate)
          : null,
      gender: patient.gender ?? null,
      avatarUrl: patient.avatarUrl ?? null,
      consultationReason: patient.consultationReason ?? null,
      accountStatus: patient.accountStatus,
      isActive: patient.isActive,
      unreadDiariesCount: patient.unreadDiariesCount,
      lastUnreadDiaryCreatedAt:
        patient.lastUnreadDiaryCreatedAt !== undefined
          ? Timestamp.fromDate(patient.lastUnreadDiaryCreatedAt)
          : null,
      msdpId: patient.msdpId ?? null,
      address: patient.address ?? null,
      postalCode: patient.postalCode ?? null,
      city: patient.city ?? null,
      country: patient.country ?? null,
      isSharedSeries: patient.isSharedSeries ?? true,
      notificationWebToken: patient.notificationWebToken ?? null,
    };
  }

  /**
   * Fix timestamp for algolia
   */
  fixBrokenTimestamp(timestamp: unknown): Timestamp | null {
    if (timestamp instanceof Timestamp || timestamp === null) {
      return timestamp;
    }
    if (typeof timestamp === 'number') {
      return Timestamp.fromMillis(timestamp);
    }
    if (typeof timestamp === 'object') {
      const tt = timestamp as Timestamp;
      return new Timestamp(tt.seconds, tt.nanoseconds);
    }

    return null;
  }

  /**
   * Fix schema timestamps for algolia
   */
  fixSchemaBrokenTimestamp(schema: PatientSchema): PatientSchema {
    schema.nextAppointment = this.fixBrokenTimestamp(schema.nextAppointment);
    schema.birthday = this.fixBrokenTimestamp(schema.birthday);
    schema.createdAt = this.fixBrokenTimestamp(schema.createdAt);
    schema.updatedAt = this.fixBrokenTimestamp(schema.updatedAt);
    schema.archivedAt = this.fixBrokenTimestamp(schema.archivedAt);
    schema.lastActiveDate = this.fixBrokenTimestamp(schema.lastActiveDate);
    schema.lastUnreadDiaryCreatedAt = this.fixBrokenTimestamp(
      schema.lastUnreadDiaryCreatedAt,
    );

    return schema;
  }

  fromSchema(schema: PatientSchema, id: string): Patient {
    return Patient.create(
      {
        firstName: schema.firstName,
        lastName: schema.lastName,
        dietId: schema.dietId,
        email: schema.email,
        lastActiveDate: schema.lastActiveDate?.toDate(),
        nextAppointment: schema.nextAppointment?.toDate(),
        birthday: schema.birthday?.toDate(),
        updatedAt: schema.updatedAt?.toDate(),
        createdAt: schema.createdAt?.toDate(),
        goals: schema.goals ?? undefined,
        archivedAt:
          schema.archivedAt instanceof Timestamp
            ? schema.archivedAt?.toDate()
            : undefined,
        avatarUrl: schema.avatarUrl ?? undefined,
        consultationReason: schema.consultationReason ?? undefined,
        gender: schema.gender ?? undefined,
        isActive: schema.isActive,
        phoneNumber: schema.phoneNumber,
        status: schema.accountStatus,
        userId: schema.userId
          ? UserId.create(new UniqueEntityID(schema.userId))
          : undefined,
        // algolia doesn't handle null or undefined
        groupId: Array.isArray(schema.groupId) ? schema.groupId : undefined,
        unreadDiariesCount: schema.unreadDiariesCount ?? 0,
        lastUnreadDiaryCreatedAt:
          schema.lastUnreadDiaryCreatedAt?.toDate() ?? undefined,
        msdpId: schema.msdpId ?? undefined,
        address: schema.address ?? undefined,
        postalCode: schema.postalCode ?? undefined,
        city: schema.city ?? undefined,
        country: schema.country ?? undefined,
        isSharedSeries: schema.isSharedSeries ?? true,
        notificationWebToken: schema.notificationWebToken ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  fromMap(map: { [key: string]: unknown }): Patient {
    const getDate = (data: unknown) =>
      typeof data === 'string' ? new Date(data) : undefined;

    return Patient.create(
      {
        firstName: map['firstName'] as string,
        lastName: map['lastName'] as string,
        dietId: map['dietId'] as string,
        email: map['email'] as string,
        goals: map['goals'] as string[] | undefined,
        lastActiveDate: getDate(map['lastActiveDate']),
        nextAppointment: getDate(map['nextAppointment']),
        birthday: getDate(map['birthday']),
        updatedAt: getDate(map['updatedAt']),
        createdAt: getDate(map['createdAt']),
        archivedAt: getDate(map['archivedAt']),
        avatarUrl: map['avatarUrl'] as string | undefined,
        consultationReason: map['consultationReason'] as string | undefined,
        gender:
          typeof map['gender'] === 'string'
            ? Gender[map['gender'] as keyof typeof Gender]
            : undefined,
        isActive:
          typeof map['isActive'] === 'boolean' ? map['isActive'] : false,
        phoneNumber:
          typeof map['phoneNumber'] === 'string'
            ? map['phoneNumber']
            : undefined,
        status:
          AccountStatus[map['accountStatus'] as keyof typeof AccountStatus],
        userId:
          typeof map['userId'] === 'string'
            ? UserId.create(new UniqueEntityID(map['userId']))
            : undefined,
        // algolia doesn't handle null or undefined
        groupId: Array.isArray(map['groupId']) ? map['groupId'] : undefined,
        unreadDiariesCount:
          typeof map['unreadDiariesCount'] === 'number'
            ? map['unreadDiariesCount']
            : 0,
        lastUnreadDiaryCreatedAt: getDate(map['lastUnreadDiaryCreatedAt']),
        address: map['address'] as string,
        postalCode: map['postalCode'] as string,
        city: map['city'] as string,
        country: map['country'] as string,
        isSharedSeries: map['isSharedSeries'] as boolean,
      },
      new UniqueEntityID(map['id'] as string),
    );
  }

  async count(dietitianId: string): Promise<PatientCounter> {
    const refColl = this.collection((ref) =>
      ref.where('dietId', '==', dietitianId),
    );
    const snap = await firstValueFrom(refColl.get());
    const docs = snap.docs;
    const activated = docs
      .map((d) => d.data())
      .filter((p) => !p.archivedAt || p.archivedAt === 'null').length;
    const archived = docs
      .map((d) => d.data())
      .filter((p) => p.archivedAt && p.archivedAt !== 'null').length;
    return { activated, archived };
  }

  async load(patientId: string): Promise<Patient> {
    return firstValueFrom(
      this.collection().doc(patientId).get().pipe(delay(200)),
    )
      .then((snap) => {
        const data = snap.data();
        if (!data) throw new PatientNotFoundException();
        return this.fromSchema(data, snap.id);
      })
      .catch((e) => {
        console.error('🔴 PatientRepo.error', e);
        return Promise.reject(e);
      });
  }

  async existsForDietitianAndPhoneNumber(
    dietitianId: string,
    phoneNumber: string,
    patientId?: string,
  ): Promise<boolean> {
    const snap = await firstValueFrom(
      this.collection((ref) =>
        ref
          .where('dietId', '==', dietitianId)
          .where('phoneNumber', '==', phoneNumber),
      ).get(),
    );

    return snap.size !== 0 && !snap.docs.some((doc) => doc.id == patientId);
  }

  async save(patient: Patient): Promise<Patient> {
    const schema = this.toSchema(patient);
    await this.collection().doc(patient.id.toString()).update(schema);
    await firstValueFrom(
      this.indexationPatientFromCloud({
        patientId: patient.id.toString(),
        index: PatientRepository.PATIENT_INDEX,
      }),
    ).catch();
    return this.fromSchema(schema, patient.patientId.id.toString());
  }

  // Fix bug of erase userId...
  async update(patientId: string, props: PatientProps): Promise<void> {
    await this.collectionProps().doc(patientId).update(props);
    await firstValueFrom(
      this.indexationPatientFromCloud({
        patientId,
        index: PatientRepository.PATIENT_INDEX,
      }),
    ).catch();
  }

  async create(patient: Patient): Promise<Patient> {
    const schema = this.toSchema(patient);
    const createdPatient = await this.collection().add(schema);
    await firstValueFrom(
      this.indexationPatientFromCloud({
        patientId: createdPatient.id,
        index: PatientRepository.PATIENT_INDEX,
      }),
    ).catch();
    return this.fromSchema(schema, createdPatient.id);
  }

  async createPatientInvitationNotification(patient: Patient): Promise<void> {
    await firstValueFrom(
      this.createPatientInvitationNotificationFromCloud({
        patientId: patient.patientId.id.toString(),
      }),
    ).catch();
  }

  async getDietitianPatients(
    dietitianId: string,
    groupId: string | null,
    includeArchived = false,
  ) {
    const snap = await firstValueFrom(
      this.collection((ref) => {
        let query = ref.where('dietId', '==', dietitianId);

        if (groupId) {
          query = query.where('groupId', 'array-contains', groupId);
        }

        if (!includeArchived) {
          query = query.where('archivedAt', '==', 'null');
        }
        return query;
      }).get(),
    );

    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async getDietitianArchivedPatients(dietitianId: string) {
    const snap = await firstValueFrom(
      this.collection((ref) => {
        let query = ref.where('dietId', '==', dietitianId);
        query = query.where('archivedAt', '!=', 'null');
        return query;
      }).get(),
    );

    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async searchPatients(
    dietitianId: string,
    query: string,
    groupId: string | null,
    archived: boolean,
    resultsPerPage: number,
    page: number,
    sort: 'createdAt' | 'updatedAt' | 'lastActiveDate' = 'updatedAt',
    algolia: boolean,
  ): Promise<PaginationResult<Patient>> {
    let index = PatientRepository.PATIENT_INDEX;
    if (sort === 'createdAt') {
      index = PatientRepository.PATIENT_CREATED_AT_DESC_INDEX;
    } else if (sort === 'lastActiveDate') {
      index = PatientRepository.PATIENT_LAST_ACTIVE_DATE_DESC_INDEX;
    }
    const results = await firstValueFrom(
      this.searchPatientsFromCloud({
        dietitianId,
        query,
        index,
        groupId,
        archived,
        resultsPerPage,
        page,
        algolia,
      }),
    );

    return {
      ...results,
      results: results.results.map(this.fromMap),
    };
  }

  async getDietitianPatientsWithUnreadDiaries(
    dietitianId: string,
    groupId: string | null,
    archived: boolean,
    maxAgeSeconds = 0,
  ) {
    const minDate =
      maxAgeSeconds > 0
        ? moment().add(-maxAgeSeconds, 'second').toDate()
        : undefined;
    const snap = await firstValueFrom(
      this.collection((ref) => {
        let query = ref.where('dietId', '==', dietitianId);

        if (groupId != null) {
          query = query.where('groupId', 'array-contains', groupId);
        }

        if (minDate) {
          query = query.where('lastUnreadDiaryCreatedAt', '>=', minDate);
        }

        query = query.orderBy('lastUnreadDiaryCreatedAt', 'desc');

        return query;
      }).get(),
    );

    let docs = snap.docs;
    if (archived) {
      docs = docs.filter((d) => d.data().archivedAt != 'null');
    } else {
      docs = docs.filter((d) => d.data().archivedAt == 'null');
    }

    return docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async getPatientGroupPatients(
    dietitianId: string,
    groupId: string,
    includeArchived = true,
  ): Promise<Patient[]> {
    const snap = await firstValueFrom(
      this.collection((ref) => {
        const query = includeArchived
          ? ref
          : ref.where('archivedAt', '==', 'null');

        return query
          .where('dietId', '==', dietitianId)
          .where('groupId', 'array-contains', groupId)
          .orderBy('lastName')
          .orderBy('firstName');
      }).get(),
    );

    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async updateBatch(patients: Patient[]) {
    const batch = this.firestore.firestore.batch();
    for (const patient of patients) {
      const schema = this.toSchema(patient);
      const ref = this.collection().doc(patient.id.toString()).ref;
      batch.set(ref, schema);
    }
    await batch.commit();
    const sync = firstValueFrom(
      this.indexationPatientsFromCloud({
        patientIds: patients.map((p) => p.id.toString()),
        index: PatientRepository.PATIENT_INDEX,
      }),
    ).catch();
    await sync;
  }

  patientValueChanges(patientId: string): Observable<Patient | undefined> {
    return this.collection()
      .doc(patientId)
      .valueChanges()
      .pipe(
        map((changes) => {
          if (changes) {
            return this.fromSchema(changes, patientId);
          } else {
            return undefined;
          }
        }),
      );
  }

  async loadByUserId(userId: string): Promise<Patient> {
    const snap = await firstValueFrom(
      this.collection((ref) => ref.where('userId', '==', userId)).get(),
    );

    if (snap.size === 0) {
      throw new Error('Patient authentifié introuvable');
    }

    if (snap.size === 1) {
      const doc = snap.docs[0];
      return this.fromSchema(doc.data(), doc.id);
    } else {
      throw new Error('Patient authentifié trouvé en doublon !');
    }
  }

  async findByPhoneNumber(phoneNumber: string | undefined) {
    const snap = await firstValueFrom(
      this.collection((ref) =>
        ref.where('phoneNumber', '==', phoneNumber),
      ).get(),
    );

    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async postLoginAction() {
    const result: any = await firstValueFrom(this.processPostLoginAction({}));
    const { id, ...props } = result;
    const patientPros: PatientProps = {
      ...props,
      userId: UserId.create(new UniqueEntityID(props.userId)),
      status: this.accountStatus(props.accountStatus),
      gender: this.gender(props.gender),
      birthday: props.birthday
        ? new Timestamp(
            props.birthday._seconds,
            props.birthday._nanoseconds,
          ).toDate()
        : undefined,
      archivedAt: props.archivedAt
        ? new Timestamp(
            props.birthday._seconds,
            props.birthday._nanoseconds,
          ).toDate()
        : undefined,
      nextAppointment: props.nextAppointment
        ? new Timestamp(
            props.nextAppointment._seconds,
            props.nextAppointment._nanoseconds,
          ).toDate()
        : undefined,
      createdAt: props.createdAt
        ? new Timestamp(
            props.createdAt._seconds,
            props.createdAt._nanoseconds,
          ).toDate()
        : undefined,
      updatedAt: props.updatedAt
        ? new Timestamp(
            props.updatedAt._seconds,
            props.updatedAt._nanoseconds,
          ).toDate()
        : undefined,
      lastActiveDate: props.lastActiveDate
        ? new Timestamp(
            props.lastActiveDate._seconds,
            props.lastActiveDate._nanoseconds,
          ).toDate()
        : undefined,
      lastUnreadDiaryCreatedAt: props.lastUnreadDiaryCreatedAt
        ? new Timestamp(
            props.lastUnreadDiaryCreatedAt._seconds,
            props.lastUnreadDiaryCreatedAt._nanoseconds,
          ).toDate()
        : undefined,
    };
    const patient = Patient.create(patientPros, new UniqueEntityID(id));
    return patient;
  }

  async postAlternativeLoginAction(
    prefix: string,
    phoneNumber: string,
    email: string,
  ) {
    const result = await firstValueFrom(
      this.processPostAlternativeLoginAction({
        prefix,
        email,
        phoneNumber,
      }),
    );
    return result as ResultAlternativeLoginAction;
  }

  async closeAccount() {
    await firstValueFrom(this.closeAccountFromCloud({}));
  }

  async checkPhoneNumber(phoneNumber: string) {
    const result = await firstValueFrom(
      this.checkPhoneNumberFromCloud({
        phoneNumber,
        secret: '02jMQbZVN8gCB0M8itk1',
      }),
    );
    return result.check;
  }

  private accountStatus(accountStatus: string): AccountStatus {
    switch (accountStatus) {
      case 'FINALIZED':
        return AccountStatus.FINALIZED;
      case 'DRAFT':
        return AccountStatus.DRAFT;
      case 'INVITATION_SENT':
        return AccountStatus.INVITATION_SENT;
      case 'BANNED':
        return AccountStatus.BANNED;
      case 'BLOCKED':
      default:
        return AccountStatus.BLOCKED;
    }
  }

  private gender(gender: any): Gender {
    switch (gender) {
      case 'WOMAN':
        return Gender.WOMAN;
      case 'MAN':
        return Gender.MAN;
      case 'NB':
      default:
        return Gender.NB;
    }
  }
}
